<template v-if="data.bankruptcies">
  <div
    v-for="(bankruptcy, index) in data.bankruptcies"
    :key="index"
    class="my-6 rounded-sm border pt-5"
  >
    <div
      class="flex items-start px-4"
      :class="{ 'border-b': isMiddeskBankruptciesContentOpened }"
      @click="toggleMiddeskBankruptciesContent"
    >
      <span class="mt-0.5">
        <dropdown-chevron :is-open="isMiddeskBankruptciesContentOpened" />
      </span>
      <div class="pb-5">
        <lf-h4>{{ t("DEALS.MIDDESK.DEBTOR") + " " + (index + 1) }}</lf-h4>
      </div>
    </div>
    <div
      v-if="isMiddeskBankruptciesContentOpened"
      class="flex flex-col px-5 py-5"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center justify-start">
          <icon-base
            :icon="IconProfile"
            :width="48"
            :height="48"
            class="mr-2 place-self-center text-disabled-medium"
          />
          <div class="ml-2 flex flex-col justify-between">
            <lf-h4>{{ bankruptcy.debtorsName || "-" }}</lf-h4>
            <span>{{ t("DEALS.MIDDESK.DEBTOR") }}</span>
          </div>
        </div>
        <div>
          <icon-base :icon="IconAddress" class="mr-2 place-self-center" />
          <info-span-copy :text="data.address?.full_address || '-'" />
        </div>
      </div>
      <div class="mt-5 flex justify-between space-x-4">
        <div
          class="flex-1 flex-col items-center justify-center rounded-sm border p-3"
        >
          <span>{{ t("DEALS.MIDDESK.CHAPTER") }}</span>
          <lf-h3>{{ bankruptcy.chapter }}</lf-h3>
        </div>
        <div
          class="flex-1 flex-col items-center justify-center rounded-sm border p-3"
        >
          <span>{{ t("DEALS.MIDDESK.CASE_NUMBER") }}</span>
          <lf-h3>{{ bankruptcy.case_number }}</lf-h3>
        </div>
        <div
          class="flex-1 flex-col items-center justify-center rounded-sm border p-3"
        >
          <span>
            {{ t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.FORMATION_DATE") }}
          </span>
          <lf-h3>
            {{ formatDateCustom(data.formation?.formation_date, "MMM d, yyy") }}
          </lf-h3>
        </div>
        <div
          class="flex-1 flex-col items-center justify-center rounded-sm border p-3"
        >
          <span>{{ t("DEALS.MIDDESK.COURT_NUMBER") }}</span>
          <lf-h3>{{ bankruptcy.court }}</lf-h3>
        </div>
      </div>
      <div
        v-if="hasCaseUpdates(bankruptcy)"
        class="mt-6 flex w-full flex-wrap border-t border-b py-6"
      >
        <div
          v-for="(caseUpdate, cuIndex) in formatCaseUpdates(
            bankruptcy.case_updates
          )"
          :key="cuIndex"
          class="flex w-1/3 flex-col"
          :class="cuIndex !== 0 && cuIndex % 3 !== 0 ? 'border-l px-3' : 'pr-3'"
        >
          <div
            v-for="caseUpdateData in caseUpdate"
            :key="caseUpdateData.guid"
            class="flex"
          >
            <div class="mr-3 mb-2 flex w-3/5 flex-wrap">
              {{ caseUpdateData.description }}
            </div>
            <div class="flex w-2/5 flex-wrap justify-end">
              <info-span-copy
                :text="formatPublishDate(caseUpdateData.pub_date)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="mt-6 flex w-full flex-col">
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.COURT_TYPE") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy :text="bankruptcy.court_type || '-'" />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.COURT_NAME") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy :text="bankruptcy.court" />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.OFFICE_NUMBER") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy :text="bankruptcy.office || '-'" />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.TRUSTEE") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy :text="bankruptcy.trustee || '-'" />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">
            {{ t("DEALS.MIDDESK.CASE_DESCRIPTION") }}:
          </div>
          <div class="flex w-3/4">
            <info-span-copy
              :text="
                hasCaseUpdates(bankruptcy)
                  ? bankruptcy.case_updates[0].description
                  : '-'
              "
            />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.CASE_GUID") }}:</div>
          <div class="flex w-3/4">
            <a
              v-if="hasCaseUpdates(bankruptcy)"
              :href="bankruptcy.case_updates[0].guid"
              target="_blank"
            >
              <icon-base height="24" width="24" :icon="IconLink" />
            </a>
            <info-span-copy v-else text="-" />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.PUBLISH_DATE") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy
              :text="
                hasCaseUpdates(bankruptcy)
                  ? formatDateCustom(
                      parseMiddeskDateToEcma(
                        bankruptcy.case_updates[0].pub_date
                      ),
                      'MMM d, yyy'
                    )
                  : '-'
              "
            />
          </div>
        </div>
        <div class="mb-2 flex">
          <div class="flex w-1/4">{{ t("DEALS.MIDDESK.FILING_DATE") }}:</div>
          <div class="flex w-3/4">
            <info-span-copy
              :text="formatDateCustom(bankruptcy.filing_date, 'MMM d, yyy')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { formatDistance } from "date-fns";
import chunk from "lodash/chunk";

import { formatDateCustom, parseMiddeskDateToEcma } from "@/helpers/formatting";

import IconAddress from "@/components/icons/IconAddress.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconProfile from "@/components/icons/sidebar/IconProfile.vue";
import InfoSpanCopy from "@/views/deals/components/InfoSpanCopy.vue";

import type {
  CaseUpdate,
  MiddeskBankruptcy,
  MiddeskBankruptcyTab,
} from "@/models/commercialData/Middesk";
import type { PropType } from "vue";

defineProps({
  data: {
    type: Object as PropType<MiddeskBankruptcyTab>,
    required: true,
  },
});
const { t } = useI18n();
const isMiddeskBankruptciesContentOpened = ref(false);

const toggleMiddeskBankruptciesContent = () => {
  isMiddeskBankruptciesContentOpened.value =
    !isMiddeskBankruptciesContentOpened.value;
};

const hasCaseUpdates = (bankruptcy: MiddeskBankruptcy) => {
  return bankruptcy.case_updates && bankruptcy.case_updates.length > 0;
};

const formatCaseUpdates = (caseUpdates: CaseUpdate[]) => {
  return chunk(caseUpdates, Math.ceil(caseUpdates.length / 3));
};

const formatPublishDate = (publishDate: string) => {
  try {
    return `${formatDistance(new Date(), new Date(publishDate))} ${t(
      "COMMON.AGO"
    )}`;
  } catch (_) {
    try {
      `${formatDistance(
        new Date(),
        new Date(parseMiddeskDateToEcma(publishDate))
      )} ${t("COMMON.AGO")}`;
    } catch (_) {
      return "-";
    }
    return "-";
  }
};
</script>
